import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import DatePicker, { registerLocale } from "react-datepicker";
import { es, fr, enGB, it } from "date-fns/locale";
import ar from "date-fns/locale/ar-MA";

import { Grid, Radio, FormControlLabel, Button } from "@mui/material";

import { PeriodType } from "../../../models/documents/period/period";

import "react-datepicker/dist/react-datepicker.css";

import { Props } from "./interfaces";
import { RadioGroup } from "./styles";

registerLocale("es", es);
registerLocale("fr", fr);
registerLocale("enGB", enGB);
registerLocale("it", it);
registerLocale("ar", ar);

const ButtonsSelect = ({ date = new Date(), onDateChange, radioValue }: Props) => {
  const { i18n, t } = useTranslation();

  const getCurrentLanguage = (): string => (i18n.language === "en" ? "enGB" : i18n.language);

  const CustomInput = forwardRef(
    (
      {
        value,
        onClick,
      }: {
        value: string;
        onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
      },
      _ref: any
    ) => (
      <Button
        variant="outlined"
        onClick={onClick}
        ref={_ref}
      >
        {value}
      </Button>
    )
  );
  const elements = [
    {
      value: PeriodType.CalendaristicYear,
      label: t("pages.datePicker.calendar_year"),
      component: (
        <DatePicker
          selected={date}
          onChange={(aDate) => onDateChange(aDate, PeriodType.CalendaristicYear)}
          customInput={React.createElement(CustomInput)}
          dateFormat="yyyy"
          locale={getCurrentLanguage()}
          showYearPicker
        />
      ),
    },
    {
      value: PeriodType.FiscalYear,
      label: t("pages.datePicker.fiscal_year"),
      component: (
        <DatePicker
          selected={date}
          onChange={(aDate) => onDateChange(aDate, PeriodType.FiscalYear)}
          customInput={React.createElement(CustomInput)}
          dateFormat="yyyy"
          locale={getCurrentLanguage()}
          showYearPicker
        />
      ),
    },
    {
      value: PeriodType.Quarter,
      label: t("pages.datePicker.quarter"),
      component: (
        <DatePicker
          selected={date}
          onChange={(aDate) => onDateChange(aDate, PeriodType.Quarter)}
          customInput={React.createElement(CustomInput)}
          dateFormat="yyyy QQQ"
          locale={getCurrentLanguage()}
          showQuarterYearPicker
        />
      ),
    },
    {
      value: PeriodType.Month,
      label: t("pages.datePicker.month"),
      component: (
        <DatePicker
          selected={date}
          onChange={(aDate) => onDateChange(aDate, PeriodType.Month)}
          customInput={React.createElement(CustomInput)}
          dateFormat="yyyy MMMM"
          locale={getCurrentLanguage()}
          showMonthYearPicker
          showFullMonthYearPicker
        />
      ),
    },
  ];

  return (
    <RadioGroup
      value={radioValue}
      onChange={(event) => onDateChange(date, event.target.value as PeriodType)}
    >
      {elements.map(({ value, label, component }, idx) => (
        <Grid key={idx}>
          <FormControlLabel
            value={value}
            label={label}
            control={<Radio />}
          />
          {radioValue === value && component}
        </Grid>
      ))}
    </RadioGroup>
  );
};

export default ButtonsSelect;
